import { useCallback, useMemo } from 'react';

import Rect from '@lyearn/core/components/Rect';
import { useTranslation } from '@lyearn/i18n';
import AsyncView from '@/components/AsyncView';
import * as ContentListModal from '@/components/ContentListModal';
import SortAndFilter from '@/components/FilterManager/SortAndFilter';
import { ModalVariant } from '@/components/Modals/types';
import { PreserveModalForm } from '@/components/PreserveModalForm';
import Table from '@/components/Table';
import useLearnerAndGroupDialog from '@/modules/corporate/components/LearnerAndGroupDialog/hooks/useLearnerAndGroupDialog';
import { Tabs } from '@/modules/corporate/components/LearnerAndGroupDialog/types';
import { getTableColumns } from '@/modules/corporate/components/LearnerAndGroupDialog/utils';
import AdminEmptyState from '@/modules/corporate/modules/admin/components/EmptyState';
import { ReviewerConfigInput } from '@/types/schema';

interface SelectReviewersDialogProps {
  reviewerConfig: ReviewerConfigInput;
  onClose: () => void;
  setReviewers: (nextValue: ReviewerConfigInput) => void;
}

function SelectReviewersDialog({
  reviewerConfig,
  onClose,
  setReviewers,
}: SelectReviewersDialogProps) {
  const { t } = useTranslation();

  const onAddLearnerAndGroup = useCallback(
    (userIds: string[]) => {
      if (userIds.length > 1) {
        setReviewers({
          ...reviewerConfig,
          userIds: userIds,
        });
      } else {
        setReviewers({
          ...reviewerConfig,
          defaultReviewerId: null,
          userIds: userIds,
        });
      }
      onClose();
    },
    [onClose, reviewerConfig, setReviewers],
  );

  const {
    data: {
      isFetching,
      data,
      fetchMore,
      selectedCount,
      error,
      asyncList: {
        methods: { onChange, onSelectAllChange, isItemSelected },
        state: { isSelectAll, isPartiallySelected },
      },
      sortAndFilterItems,
      filterManager: {
        state: { sort, filter, search },
        methods: { setSort, setFilter, setSearch, clearSearch, clearFilters },
      },
    },
    onSubmit,
    inViewTab,
    isSubmitting,
  } = useLearnerAndGroupDialog({
    onAddLearnerAndGroup,
    selectedLearnerIds: reviewerConfig.userIds ?? [],
    selectedUserGroupIds: [],
  });

  const tableColumns = useMemo(
    () =>
      getTableColumns({
        t,
        isSelectAll,
        isPartiallySelected,
        onSelectAll: onSelectAllChange,
        inViewTab: Tabs.Learners,
        isItemSelected,
        toggle: onChange,
        selectedCount,
        disabled: false,
      }),
    [
      isItemSelected,
      isPartiallySelected,
      isSelectAll,
      onSelectAllChange,
      selectedCount,
      t,
      onChange,
    ],
  );

  return (
    <PreserveModalForm showDiscardDialog={false} onClose={onClose} onDiscard={() => {}}>
      <ContentListModal.Main
        action={{
          text: t('Common:Actions.Add'),
          discardText: t('Common:Cancel'),
          onSubmit,
          isSubmitting,
        }}
        header={{
          title: t('Exercise:AssignReviewers'),
          description: t('Exercise:AssignReviewersDescription'),
        }}
        items={data}
        searchBar={{
          value: search,
          onChange: setSearch,
          selectedCount,
          placeholder: t('Common:Search'),
        }}
        sortComponent={
          sortAndFilterItems ? (
            <SortAndFilter
              className="mt-20 mb-12 px-8"
              filter={filter}
              setFilter={setFilter}
              setSort={setSort}
              sort={sort}
              sortAndFilterItems={sortAndFilterItems}
            />
          ) : undefined
        }
        variant={ModalVariant.ExtraWide}
        onClose={onClose}>
        <AsyncView
          data={data}
          error={error}
          fetching={isFetching}
          renderData={() => (
            <Rect>
              {({ width, height }) => (
                <Table
                  columns={tableColumns}
                  data={data}
                  headerClassName="sticky top-0 !bg-bg-primary"
                  headerHeight={inViewTab === Tabs.Learners ? 0 : 32}
                  height={height}
                  ignoreFunctionInColumnCompare={false}
                  rowClassName="flex-1 w-full"
                  rowHeight={inViewTab === Tabs.Learners ? 81 : 83}
                  rowKey="id"
                  width={width}
                  onEndReached={fetchMore}
                  onEndReachedThreshold={1000}
                />
              )}
            </Rect>
          )}
          renderEmptyState={() => (
            <AdminEmptyState
              clearFilters={clearFilters}
              clearSearch={clearSearch}
              description={''}
              filter={filter}
              search={search}
              showIllustrationIcon={false}
              title={t('Admin:NoLearningContentDescription')}
            />
          )}
        />
      </ContentListModal.Main>
    </PreserveModalForm>
  );
}

export default SelectReviewersDialog;
